/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import i18n from '../i18n/i18n.ts';
import {
  ChatMessageInterface,
  CrystalGuest,
  CrystalState,
  MolecularContainer,
  MoleculeInterface,
  MoleculeTransform,
  ParticleResolution,
  ProjectState,
  Range,
} from '../types.ts';
import { MolecularProperties } from '../models/MolecularProperties.ts';
import { ThermostatGroupSpec } from '../models/thermostatGroups.ts';
import { Filter, FilterType } from '../Filter.ts';
import {
  DataColoring,
  DEFAULT_CAMERA_POSITION,
  DEFAULT_CAMERA_ROTATION,
  DEFAULT_CAMERA_UP,
  DEFAULT_PAN_CENTER,
  GalleryViewMode,
  GraphType,
  isBiomoleculeProject,
  isCrystalCoreProject,
  LabelType,
  MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL,
  ProjectType,
  SpaceshipDisplayMode,
} from '../constants.ts';
import { MolecularViewerColoring, MolecularViewerMaterial, MolecularViewerStyle } from '../view/displayOptions.ts';
import { Util } from '../Util.ts';
import { Molecule } from '../models/Molecule.ts';
import { SPEED_BIN_NUMBER } from '../models/physicalConstants.ts';
import { GravitationalField } from '../models/GravitationalField.ts';
import { DEFAULT_AI_PROVIDER, demoteRetiredProvider, loadAiProviderPreference } from './aiProviderPreference.ts';
import { chemicalFormula, fitSupercellToCap, takenCrystalNames, uniqueCrystalName } from '../crystal/Crystal.ts';

// ProjectState fields supplied per-project at creation/load time, not defaulted.
export type ProjectRuntimeFields = 'key' | 'owner' | 'time' | 'timestamp' | 'type' | 'title';

// IANA time-zone identifiers that map to the United States and its territories.
// Used only as a HEURISTIC to pick the default AI provider for a NEW project —
// there is no reliable, privacy-respecting way to know a user's actual country
// (let alone citizenship) from the browser. Time zone is the best free, offline,
// language-independent proxy for current location (unlike navigator.language,
// which many non-US browsers report as en-US). VPNs / travelers can defeat it,
// but the result is only a user-overridable default, so an occasional miss is
// harmless. Keep this list in sync with the common US zones; unlisted zones are
// simply treated as non-US (routed to DeepSeek).
const US_TIME_ZONES = new Set<string>([
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Chicago',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
  // Territories
  'America/Puerto_Rico',
  'America/St_Thomas',
  'Pacific/Guam',
  'Pacific/Saipan',
  'Pacific/Pago_Pago',
]);

// True when the browser's time zone looks like the United States. Defensive:
// any failure (old browser, blocked Intl) falls back to `true` so US stays the
// safe default and we never route someone to the wrong provider on an error.
export const isLikelyUSLocation = (): boolean => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? US_TIME_ZONES.has(tz) : true;
  } catch {
    return true;
  }
};

// Provider a NEW project defaults to. If the user has previously picked a
// provider (remembered globally in localStorage), a new project inherits that
// choice. Otherwise fall back to the location heuristic: US users keep the
// OpenAI default (PROJECT_DEFAULTS.aiProvider, gpt-6-luna); everyone else
// defaults to DeepSeek Flash, which is reachable and cheap outside the US.
// Only affects the default — saved projects keep their stored provider and the
// user can change it any time in settings.
const defaultAiProviderForLocation = (): ProjectState['aiProvider'] =>
  loadAiProviderPreference() ?? (isLikelyUSLocation() ? PROJECT_DEFAULTS.aiProvider : 'deepseek');

// Piston plate mass (amu) when the project does not say: a LIGHT plate that
// visibly moves in an interactive MD run. 0 means auto/HEAVY (≥ 1000 amu), so
// every `pistonMass ?? …` fallback (controls, settings, tool reply) must use
// THIS, never 0, or the engine runs a plate the UI does not show.
export const DEFAULT_PISTON_MASS_AMU = 100;

// MD timestep (fs) when the project does not say. Every `timeStep ?? …` fallback
// (controls, settings, viewer, agent replies) must use THIS: the worker steps
// at it, so a different fallback elsewhere would tell the agent a dt the engine
// is not using (and its steps = ps × 1000 / dt conversions would be off).
export const DEFAULT_TIME_STEP_FS = 1;

// Fields that have stable defaults. Single source of truth — used by both
// createDefaultProjectState() and the Firestore loader (fetchProject).
export const PROJECT_DEFAULTS: Omit<ProjectState, ProjectRuntimeFields> = {
  description: null,
  molecules: [] as MoleculeInterface[],
  numberOfColumns: 3,
  selectedMolecule: null,
  ligand: null,
  protein: null,
  biomolecules: [],
  selectedBiomoleculeName: null,
  // Legacy single-structure field; kept in defaults so it round-trips through
  // mergeWithDefaults and migrateBiomoleculesField can fold it into the set.
  biomolecule: null,
  // Biomolecule Lab study-tool state — listed here so mergeWithDefaults restores
  // the saved values from Firestore on load (absent → these defaults). See
  // ProjectState for the field docs; saved by prepareProjectForSave, rehydrated
  // by bioStructureViewer.
  bioInspectMode: false,
  bioPicks: [] as ProjectState['bioPicks'],
  bioPartialMD: null as ProjectState['bioPartialMD'],
  bioSimAnalysisVisible: false,
  bioPartialMDSnapshot: null as ProjectState['bioPartialMDSnapshot'],
  bioSimEnsemble: 'NVT' as ProjectState['bioSimEnsemble'],
  bioSimSolvent: true,
  bioLigand: null as ProjectState['bioLigand'],
  bioLigandTransform: { x: 0, y: 0, z: 0, euler: [0, 0, 0] } as ProjectState['bioLigandTransform'],
  bioLigandReplaces: null as ProjectState['bioLigandReplaces'],
  selectedProperty: null,
  autoscaleGraph: false,
  dataColoring: DataColoring.ALL,
  sortDescending: false,
  ranges: [] as Range[],
  filters: [] as Filter[],
  enableFilters: false,
  hiddenProperties: [
    'atomCount',
    'bondCount',
    'heavyAtomCount',
    'complexity',
    'density',
    'meltingPoint',
    'boilingPoint',
    'fsp3',
    'ringCount',
    'aromaticRingCount',
    'qed',
  ],
  // Listed in defaults so mergeWithDefaults preserves the saved `true` flag
  // across reloads — otherwise the retroactive-hide migration re-runs every
  // load and clobbers the user's choice to unhide one of those four keys.
  legacyHiddenPropertiesPadded: false,
  // Crystal-builder hidden-property list. `null` means "use the canonical
  // crystal default" (resolveCrystalHidden handles that). Listed here so
  // mergeWithDefaults preserves the saved array across reloads — without
  // this entry the field was silently dropped on load and the user's
  // visibility toggles snapped back to the default on every reopen.
  hiddenCrystalProperties: null as string[] | null,
  xAxisNameScatterPlot: 'atomCount',
  yAxisNameScatterPlot: 'bondCount',
  xFormula: 'x',
  yFormula: 'y',
  xMinScatterPlot: 0,
  xMaxScatterPlot: 100,
  yMinScatterPlot: 0,
  yMaxScatterPlot: 100,
  xLinesScatterPlot: false,
  yLinesScatterPlot: false,
  lineWidthScatterPlot: 1,
  dotSizeScatterPlot: 4,
  sortDataScatterPlot: 'None',
  regressionDegree: 1,
  numberOfMostSimilarMolecules: 5,

  cameraPosition: DEFAULT_CAMERA_POSITION,
  cameraRotation: DEFAULT_CAMERA_ROTATION,
  cameraUp: DEFAULT_CAMERA_UP,
  panCenter: DEFAULT_PAN_CENTER,
  navPosition: DEFAULT_CAMERA_POSITION,
  navRotation: DEFAULT_CAMERA_ROTATION,
  navUp: DEFAULT_CAMERA_UP,
  navTarget: DEFAULT_PAN_CENTER,

  hideGallery: false,
  isPublic: false,

  chamberViewerPercentWidth: 50,
  chamberViewerAxes: true,
  chamberViewerStyle: MolecularViewerStyle.BallAndStick,
  chamberViewerMaterial: MolecularViewerMaterial.Diffuse,
  chamberViewerColoring: MolecularViewerColoring.Element,
  chamberViewerFoggy: false,
  chamberViewerBackground: 'black',
  chamberViewerSelector: 'all',
  chamberTemperatureKevin: false,

  rotationStep: Util.toRadians(5),
  translationStep: 1,

  ligandTransform: { x: 0, y: 0, z: 0, euler: [0, 0, 0] } as MoleculeTransform,
  ligandConformation: null as number[] | null,
  ligandFlexible: true,
  ligandGhostVisible: false,
  bindingSiteCenter: null as number[] | null,
  selectedPocketInfo: null as unknown | null,
  pharmacophoreGuided: false,
  screeningResults: [] as unknown[],
  screeningAnalysisResult: null as string | null,
  searchRadius: 8,
  searchRegionVisible: true,
  pocketMarkersVisible: true,
  ligandVelocity: [0, 0, 0],

  spaceshipDisplayMode: SpaceshipDisplayMode.NONE,
  spaceshipThrust: 1,
  spaceshipRoll: 0,
  spaceshipPitch: 0,
  spaceshipYaw: 0,
  spaceshipX: 0,
  spaceshipY: 0,
  spaceshipZ: 0,
  spaceshipSize: 1,

  projectViewerStyle: MolecularViewerStyle.Stick,
  projectViewerOverlayStyle: null as MolecularViewerStyle | null,
  proteinOverlayStyle: null as MolecularViewerStyle | null,
  projectViewerMaterial: MolecularViewerMaterial.Soft,
  projectViewerTranslucentOpacity: 0.5,
  projectViewerBackground: '#ffffff',

  graphType: GraphType.PARALLEL_COORDINATES,
  bioChartType: 'metrics',
  labelType: LabelType.NAME,
  galleryViewMode: GalleryViewMode.GRID,
  propertiesPaneFraction: 0.5,
  propertiesPaneCollapsed: false,
  comparedMoleculeNames: [] as string[],
  comparedCrystalNames: [] as string[],

  xyPlaneVisible: false,
  yzPlaneVisible: false,
  xzPlaneVisible: false,
  xyPlanePosition: 0,
  yzPlanePosition: 0,
  xzPlanePosition: 0,

  testMolecules: [] as Molecule[],
  molecularContainer: { lx: 50, ly: 50, lz: 50 } as MolecularContainer,
  gravitationalAcceleration: 0,
  rodFieldStrength: 0,
  gravityDirection: GravitationalField.VIEWER_COORDINATE_SYSTEM,
  pistonEnabled: false,
  pistonPressure: 0.1, // MPa (~1 atm)
  pistonMass: DEFAULT_PISTON_MASS_AMU, // amu — a LIGHT plate that visibly moves in an interactive MD run. A heavy
  // plate (the 0 = auto-heavy default, ≥1000 amu) barely budges under gas collisions, so the
  // barostat looks frozen and is effectively useless. 0 still selects the auto-heavy mass.
  pistonLangevin: false,
  // γ in 1/fs; 0 = auto — critical damping for the live load (MolecularDynamics.
  // pistonEffectiveDamping). The old fixed 0.01 left the Gas Laws plate ~10×
  // overdamped, creeping for hundreds of ps whenever the pressure was lowered.
  pistonDamping: 0,
  pistonSnapshot: null as ProjectState['pistonSnapshot'],
  barostatEnabled: false,
  barostatPressure: 0.1, // MPa (~1 atm)
  barostatTau: 2, // ps
  molecularContainerVisible: true,
  // Listed here so mergeWithDefaults keeps it on load (it only copies known keys).
  chamberUnitCellsHidden: false,
  vdwBondsVisible: false,
  vdwBondCutoffRelative: 0.5,
  hBondsVisible: false,
  disulfideBondsVisible: false,
  pharmacophoreVisible: false,
  momentumVisible: false,
  momentumScaleFactor: 1,
  kineticEnergyColorVisible: false,
  forceVisible: false,
  forceScaleFactor: 1,
  kineticEnergyScaleFactor: 1,
  energyGraphVisible: false,
  speedGraphVisible: false,
  irSpectrumPanelVisible: false,
  analysisPanelVisible: false,
  activeAnalysisTab: 'energy' as const,
  measurementTracks: [],
  speedGraphMaxX: 0,
  speedGraphMaxY: 0,
  speedGraphSortByMolecule: false,
  speedGraphBinNumber: SPEED_BIN_NUMBER,
  angularBondsVisible: false,
  torsionalBondsVisible: false,

  timeStep: DEFAULT_TIME_STEP_FS, // fs — standard classical-MD default; reactive mode sub-cycles to MAX_REACTIVE_TIMESTEP on its own
  refreshInterval: 100, // MD steps per rendered frame — 20 advanced too little sim time per frame (sluggish); 100 runs 5× faster
  collectInterval: 100,
  constantTemperature: false,
  temperature: 300,
  thermostat: 'berendsen',
  thermostatGroups: [] as ThermostatGroupSpec[],
  constantPressure: false,
  pressure: 1,
  reactiveMode: false,
  langevinThermostat: false,
  langevinFriction: 0.02,
  reactiveFreezeCharges: true,
  reactiveCharges: true,
  quadrupoleSites: false,
  dispersionTail: false,
  beadSolvent: 'water' as const,
  particleResolution: 'all-atom' as ParticleResolution,

  navigationView: false,
  showInstructionPanel: false,

  reasoningEffort: 'medium',
  aiProvider: DEFAULT_AI_PROVIDER,
  agentMode: false,
  numberOfStepsToRemember: 5,
  showPrompts: false,
  generateMoleculePrompt: 'Generate a novel molecule similar to benzene.',
  generateMoleculePromptHistory: [] as string[],
  generatedMolecularProperties: {} as { [key: string]: MolecularProperties },
  chatMessages: [] as ChatMessageInterface[],
  // Listed here so mergeWithDefaults round-trips the agent delta baseline from
  // Firestore (see ProjectState.lastAgentSnapshot).
  lastAgentSnapshot: null as ProjectState['lastAgentSnapshot'],
  // Rolling summary of older chat turns (see ProjectState.chatSummary); listed
  // so mergeWithDefaults round-trips it from Firestore.
  chatSummary: null as ProjectState['chatSummary'],
  // Last sweep's config (see ProjectState.lastSweep); listed so
  // mergeWithDefaults round-trips it from Firestore — otherwise "repeat the last
  // experiment" loses the recorded range when a saved project is reopened.
  lastSweep: null as ProjectState['lastSweep'],

  generateCrystalPrompt: 'Generate a CIF for NaCl rock salt at room temperature.',
  generateCrystalPromptHistory: [] as string[],

  crystals: [] as CrystalState[],
  selectedCrystal: null,
  chamberCrystal: null,
  chamberSupercell: null,
  chamberGuests: [] as CrystalGuest[],
  // Live MD snapshot (coordinates + velocities + step) of the chamber
  // crystal, restored on load so a paused simulation resumes in place.
  // Listed here so mergeWithDefaults round-trips it from Firestore.
  chamberCrystalDynamics: null as ProjectState['chamberCrystalDynamics'],
  // Semiconductor Design devices (heterostructure / superlattice recipes)
  // and which one is selected / in the lab. Listed here because
  // mergeWithDefaults drops any key without a default: without these a
  // saved project came back with no device cards, and the lab — whose
  // synthetic chamberCrystal DID round-trip — lost the chamberDevice gate
  // that keeps a layered stack view-only, so it built a generic MD engine on
  // the stack and showed Play.
  devices: [] as NonNullable<ProjectState['devices']>,
  selectedDevice: null as ProjectState['selectedDevice'],
  chamberDevice: null as ProjectState['chamberDevice'],

  polymerMonomerSdf: null as string | null,
  polymerMonomerName: null as string | null,
  polymerHeadAtomIndex: null as number | null,
  polymerTailAtomIndex: null as number | null,
  polymerHeadHydrogenIndex: null as number | null,
  polymerTailHydrogenIndex: null as number | null,
  polymerChainLength: 20,
  polymerTorsionJitterDeg: 30,
  polymerLastGeneratePrompt: null as string | null,
  polymerChainCount: 1,
  polymerBeadSequence: 'B',
  polymerBeadBondLength: 0.97,
  polymerProperties: null as ProjectState['polymerProperties'],
};

// Merge a partial Firestore document over PROJECT_DEFAULTS. Iterates over
// default keys (so unknown fields in `data` are dropped) and skips undefined
// values (so missing-from-doc fields fall through to the default).
export const mergeWithDefaults = <T extends object>(defaults: T, data: Record<string, unknown>): T => {
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T)[]) {
    const value = data[key as string];
    if (value !== undefined) {
      (out as Record<string, unknown>)[key as string] = value;
    }
  }
  return out;
};

// Property keys that should be hidden on existing projects that predate
// the feature. Without this, an old project's persisted hiddenProperties
// array (saved before these keys existed) would leave the new graphs /
// axes visible by default, which is the opposite of the chosen default.
// Guarded by legacyHiddenPropertiesPadded so the migration runs exactly
// once per project — after the flag is set, the user's subsequent toggles
// (which may remove a key from hiddenProperties) are fully respected.
const RETROACTIVELY_HIDDEN_PROPERTIES = ['fsp3', 'ringCount', 'aromaticRingCount', 'qed'];

// One-shot migration: fold a legacy single `biomolecule` into the `biomolecules`
// collection (the Protein/DNA-RNA labs became multi-structure). Idempotent —
// once cleared, it no-ops. Runs on every project load (persist + Firestore).
export const migrateBiomoleculesField = (state: {
  biomolecule?: MoleculeInterface | null;
  biomolecules?: MoleculeInterface[];
  selectedBiomoleculeName?: string | null;
}): void => {
  if (state.biomolecule) {
    if (!state.biomolecules || state.biomolecules.length === 0) {
      state.biomolecules = [state.biomolecule];
      if (!state.selectedBiomoleculeName) state.selectedBiomoleculeName = state.biomolecule.name ?? null;
    }
    state.biomolecule = null;
  }
  if (!state.biomolecules) state.biomolecules = [];
  backfillBiomoleculeTimestamps(state);
};

// Give every structure a creation timestamp so the CHAT view can order the
// curated set chronologically (and interleave it with the conversation) instead
// of clumping every untimestamped card at one value. Structures added from now
// on are stamped at add-time (addBiomoleculeStructure); this backfills the ones
// saved before timestamping existed. Their true add-times weren't recorded, so
// we synthesize small ascending sentinels in gallery order — values that sort
// before any real (post-fix) timestamp or chat message, preserving the existing
// front-of-thread gallery order. Idempotent: only a missing/zero timestamp is
// filled, and the values persist on the next save. Runs on every project load.
export const backfillBiomoleculeTimestamps = (state: { biomolecules?: MoleculeInterface[] }): void => {
  const list = state.biomolecules;
  if (!list || list.length === 0) return;
  list.forEach((b, i) => {
    if (!b.timestamp) b.timestamp = i + 1;
  });
};

// One-shot migration: rewrite a retired aiProvider onto the tier that actually
// runs, so the stored value stops being a dead alias. demoteRetiredProvider is
// the single source of truth for what replaces what — DeepSeek collapsed
// v4-flash + v4-pro into one tier on 2026-09-10, so 'deepseek-pro' becomes plain
// 'deepseek' (deepseek-flash) and stays in the family; other retired tiers land
// on the default. Idempotent: a current provider maps to itself. Runs on every
// project load (persist + Firestore). It does NOT mark the project dirty — the
// corrected value rides along on the user's next save, which is enough, since
// every call site already resolves the same way at run time.
export const migrateRetiredAiProvider = (state: { aiProvider?: ProjectState['aiProvider'] }): void => {
  if (!state.aiProvider) return;
  const replacement = demoteRetiredProvider(state.aiProvider);
  if (replacement !== state.aiProvider) state.aiProvider = replacement;
};

// Give every unnamed gallery crystal a name — its formula, made unique
// ("NaCl", "NaCl (2)"). Gallery crystals are addressed BY NAME everywhere
// (delete, rename, the editor, Compare, the lab link), so an unnamed one —
// only older saved projects have them; every add path names its crystal now —
// could be neither deleted nor renamed, and the agent's delete reported
// success while doing nothing. The lab copy and the selection get the name of
// the gallery crystal they are a copy of (same cell), so their links survive.
// Idempotent; runs on every project load (persist + Firestore).
export const migrateUnnamedCrystals = (ps: {
  crystals?: CrystalState[] | null;
  chamberCrystal?: CrystalState | null;
  selectedCrystal?: CrystalState | null;
}): void => {
  const list = ps.crystals ?? [];
  const sameCell = (a: CrystalState, b: CrystalState) =>
    JSON.stringify([a.lattice, a.basis]) === JSON.stringify([b.lattice, b.basis]);
  const named: CrystalState[] = [];
  for (const c of list) {
    if (c.name) continue;
    c.name = uniqueCrystalName(chemicalFormula(c.basis ?? []) || 'Crystal', takenCrystalNames(ps));
    named.push(c);
  }
  const lab = ps.chamberCrystal;
  if (lab && !lab.name) {
    const twin = named.find((c) => sameCell(c, lab));
    lab.name = twin?.name ?? uniqueCrystalName(chemicalFormula(lab.basis ?? []) || 'Crystal', takenCrystalNames(ps));
  }
  const sel = ps.selectedCrystal;
  if (sel && !sel.name) {
    const twin = named.find((c) => sameCell(c, sel));
    if (twin) sel.name = twin.name;
  }
};

// A crystal-core project (Crystal Builder, Semiconductor Design, Nanomaterial
// Design) should never open with an empty lab while its gallery has structures:
// examples open chat-first, where nothing is selected, so the lab would show only
// the "drag one here" hint. When the saved lab is empty, put the selected gallery
// crystal — else the newest one — in it, at 1×1×1 for a finite nanostructure and
// at the default 2×2×2 (shrunk under the atom cap) for a periodic cell, and make
// it the selection so the gallery and the lab agree. A device in the lab, or a
// gallery with nothing in it, is left alone. Idempotent; runs on every project
// load (persist + Firestore), like the migrations above. Not marked as a change:
// the filled lab rides along on the next save.
export const fillEmptyLabFromGallery = (
  ps: {
    crystals?: CrystalState[] | null;
    chamberCrystal?: CrystalState | null;
    chamberSupercell?: { nx: number; ny: number; nz: number } | null;
    chamberDevice?: unknown;
    selectedCrystal?: CrystalState | null;
    selectedCrystalNames?: string[] | null;
  },
  type: ProjectType | undefined | null,
): void => {
  if (!isCrystalCoreProject(type)) return;
  if (ps.chamberCrystal || ps.chamberDevice) return;
  const list = ps.crystals ?? [];
  if (list.length === 0) return;
  const selectedName = ps.selectedCrystal?.name;
  let pick = selectedName ? list.find((c) => c.name === selectedName) : undefined;
  if (!pick) {
    pick = list[list.length - 1];
    for (const c of list) if ((c.timestamp ?? 0) > (pick.timestamp ?? 0)) pick = c;
  }
  const lab: CrystalState = { ...pick };
  ps.chamberCrystal = lab;
  ps.chamberSupercell = pick.finite
    ? { nx: 1, ny: 1, nz: 1 }
    : (({ nx, ny, nz }) => ({ nx, ny, nz }))(
        fitSupercellToCap(
          pick.basis?.length ?? 0,
          pick.lattice,
          { nx: 2, ny: 2, nz: 2 },
          MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL,
        ),
      );
  if (!ps.selectedCrystal) {
    ps.selectedCrystal = { ...pick };
    if (pick.name && !(ps.selectedCrystalNames ?? []).includes(pick.name)) {
      ps.selectedCrystalNames = [...(ps.selectedCrystalNames ?? []), pick.name];
    }
  }
};

export const padHiddenPropertiesForLegacyProjects = (state: {
  hiddenProperties?: string[] | null;
  legacyHiddenPropertiesPadded?: boolean;
}): void => {
  if (state.legacyHiddenPropertiesPadded) return;
  // Always replace the array with a fresh copy — mergeWithDefaults shallow-
  // copies the defaults object, so when data.hiddenProperties is undefined,
  // state.hiddenProperties ends up sharing the same reference as the global
  // PROJECT_DEFAULTS.hiddenProperties. Mutating it would pollute every later
  // project load with this project's padded list.
  const base = state.hiddenProperties ? [...state.hiddenProperties] : [];
  for (const key of RETROACTIVELY_HIDDEN_PROPERTIES) {
    if (!base.includes(key)) base.push(key);
  }
  state.hiddenProperties = base;
  state.legacyHiddenPropertiesPadded = true;
};

export class ProjectUtil {
  static createDefaultProjectState(type?: ProjectType): ProjectState {
    const resolvedType = type ?? ProjectType.MOLECULAR_MODELING;
    // Polymer Builder hides small-molecule / drug-likeness descriptors —
    // those characterize how a drug candidate behaves in the body, which
    // doesn't apply to a bulk polymer chain. We keep the structural axes
    // (atomCount, bondCount, heavyAtomCount, molecularMass, complexity,
    // rotatableBondCount, ringCount, aromaticRingCount) and the bulk
    // axes that the Predict button populates (density, meltingPoint,
    // boilingPoint). Hidden set is union of the molecular default (which
    // hides verbose / niche fields) plus drug-class fields.
    const polymerHiddenProperties = [
      'logP',
      'hydrogenBondDonorCount',
      'hydrogenBondAcceptorCount',
      'polarSurfaceArea',
      'fsp3',
      'qed',
      // Bertz / PubChem complexity is a small-molecule structural descriptor
      // (and an "AI-only" field that isn't computed from the SDF for AI-
      // generated monomers) — hiding it for polymer projects prevents the
      // parallel-coords trace from being cut by a missing value.
      'complexity',
    ];
    return {
      ...PROJECT_DEFAULTS,
      key: '',
      owner: null,
      time: '',
      timestamp: -1,
      type: resolvedType,
      title: null,
      // Route non-US users to DeepSeek Flash by default (heuristic, by time
      // zone); US users keep the OpenAI default. User-overridable in settings.
      aiProvider: defaultAiProviderForLocation(),
      // Polymer Builder defaults to the polymer-specific analysis tab — that's
      // the whole point of having a project-type-scoped tab. Everything else
      // keeps the global 'energy' default established by PROJECT_DEFAULTS.
      activeAnalysisTab: resolvedType === ProjectType.POLYMER_BUILDER ? 'polymer' : PROJECT_DEFAULTS.activeAnalysisTab,
      hiddenProperties:
        resolvedType === ProjectType.POLYMER_BUILDER ? polymerHiddenProperties : PROJECT_DEFAULTS.hiddenProperties,
      // Biomolecules (proteins / DNA / RNA) are thousands of atoms — ball-and-
      // stick is unreadable at that scale. Default the lab viewer to Cartoon,
      // the standard secondary-structure representation.
      chamberViewerStyle: isBiomoleculeProject(resolvedType)
        ? MolecularViewerStyle.Cartoon
        : PROJECT_DEFAULTS.chamberViewerStyle,
      // Element coloring is meaningless on a whole biomolecule (a cartoon is one
      // color per atom-type, which just reads as "carbon"). Default to Residue
      // coloring — per amino-acid for proteins, per base for nucleic acids —
      // which is the standard, informative view at this scale.
      chamberViewerColoring: isBiomoleculeProject(resolvedType)
        ? MolecularViewerColoring.Residue
        : PROJECT_DEFAULTS.chamberViewerColoring,
      // Biomolecule labs split the gallery column between the Q&A assistant
      // (top) and the structure analysis panel (bottom); default to a
      // chat-dominant split since the assistant is the primary tool.
      propertiesPaneFraction: isBiomoleculeProject(resolvedType) ? 0.32 : PROJECT_DEFAULTS.propertiesPaneFraction,
      // Every project type EXCEPT Molecular Modeling opens AI-first: a NEW
      // project starts in CHAT mode with the graph/analysis pane collapsed, so
      // the assistant is the immediate entry point and the comparative graphs
      // are a secondary tool the user expands when needed. This now applies to
      // every project type, Molecular Modeling included. (Only affects newly
      // created projects — existing saved projects load through the merge path
      // and keep whatever layout they were saved with.)
      galleryViewMode: GalleryViewMode.CHAT,
      propertiesPaneCollapsed: true,
    };
  }

  static getVariables(hidden: string[]): string[] {
    const a: string[] = [];
    if (!hidden?.includes('atomCount')) a.push('atomCount');
    if (!hidden?.includes('bondCount')) a.push('bondCount');
    if (!hidden?.includes('molecularMass')) a.push('molecularMass');
    if (!hidden?.includes('logP')) a.push('logP');
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push('hydrogenBondDonorCount');
    if (!hidden?.includes('hydrogenBondAcceptorCount')) a.push('hydrogenBondAcceptorCount');
    if (!hidden?.includes('rotatableBondCount')) a.push('rotatableBondCount');
    if (!hidden?.includes('polarSurfaceArea')) a.push('polarSurfaceArea');
    if (!hidden?.includes('heavyAtomCount')) a.push('heavyAtomCount');
    if (!hidden?.includes('complexity')) a.push('complexity');
    if (!hidden?.includes('density')) a.push('density');
    if (!hidden?.includes('meltingPoint')) a.push('meltingPoint');
    if (!hidden?.includes('boilingPoint')) a.push('boilingPoint');
    if (!hidden?.includes('fsp3')) a.push('fsp3');
    if (!hidden?.includes('ringCount')) a.push('ringCount');
    if (!hidden?.includes('aromaticRingCount')) a.push('aromaticRingCount');
    if (!hidden?.includes('qed')) a.push('qed');
    return a;
  }

  static getTitles(hidden: string[], l: { lng: string }): string[] {
    const a: string[] = [];
    if (!hidden?.includes('atomCount')) a.push(i18n.t('projectPanel.AtomCount', l));
    if (!hidden?.includes('bondCount')) a.push(i18n.t('projectPanel.BondCount', l));
    if (!hidden?.includes('molecularMass')) a.push(i18n.t('projectPanel.MolecularMass', l));
    if (!hidden?.includes('logP')) a.push('log P');
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push(i18n.t('projectPanel.HydrogenBondDonorCountShort', l));
    if (!hidden?.includes('hydrogenBondAcceptorCount'))
      a.push(i18n.t('projectPanel.HydrogenBondAcceptorCountShort', l));
    if (!hidden?.includes('rotatableBondCount')) a.push(i18n.t('projectPanel.RotatableBondCountShort', l));
    if (!hidden?.includes('polarSurfaceArea')) a.push(i18n.t('projectPanel.PolarSurfaceAreaShort', l));
    if (!hidden?.includes('heavyAtomCount')) a.push(i18n.t('projectPanel.HeavyAtomCountShort', l));
    if (!hidden?.includes('complexity')) a.push(i18n.t('projectPanel.Complexity', l));
    if (!hidden?.includes('density')) a.push(i18n.t('projectPanel.Density', l));
    if (!hidden?.includes('meltingPoint')) a.push(i18n.t('projectPanel.MeltingPoint', l));
    if (!hidden?.includes('boilingPoint')) a.push(i18n.t('projectPanel.BoilingPoint', l));
    if (!hidden?.includes('fsp3')) a.push(i18n.t('projectPanel.Fsp3', l));
    if (!hidden?.includes('ringCount')) a.push(i18n.t('projectPanel.RingCount', l));
    if (!hidden?.includes('aromaticRingCount')) a.push(i18n.t('projectPanel.AromaticRingCountShort', l));
    if (!hidden?.includes('qed')) a.push(i18n.t('projectPanel.QED', l));
    return a;
  }

  static getPropertyName(property: string, l: { lng: string }): string | undefined {
    if (property === 'atomCount') return i18n.t('projectPanel.AtomCount', l);
    if (property === 'bondCount') return i18n.t('projectPanel.BondCount', l);
    if (property === 'molecularMass') return i18n.t('projectPanel.MolecularMass', l);
    if (property === 'logP') return 'log P';
    if (property === 'hydrogenBondDonorCount') return i18n.t('projectPanel.HydrogenBondDonorCountShort', l);
    if (property === 'hydrogenBondAcceptorCount') return i18n.t('projectPanel.HydrogenBondAcceptorCountShort', l);
    if (property === 'rotatableBondCount') return i18n.t('projectPanel.RotatableBondCountShort', l);
    if (property === 'polarSurfaceArea') return i18n.t('projectPanel.PolarSurfaceAreaShort', l);
    if (property === 'heavyAtomCount') return i18n.t('projectPanel.HeavyAtomCountShort', l);
    if (property === 'complexity') return i18n.t('projectPanel.Complexity', l);
    if (property === 'density') return i18n.t('projectPanel.Density', l);
    if (property === 'meltingPoint') return i18n.t('projectPanel.MeltingPoint', l);
    if (property === 'boilingPoint') return i18n.t('projectPanel.BoilingPoint', l);
    if (property === 'fsp3') return i18n.t('projectPanel.Fsp3', l);
    if (property === 'ringCount') return i18n.t('projectPanel.RingCount', l);
    if (property === 'aromaticRingCount') return i18n.t('projectPanel.AromaticRingCountShort', l);
    if (property === 'qed') return i18n.t('projectPanel.QED', l);
    // Fall through to the crystal-property namespace so consumers that
    // only call this molecule-side helper (legacy graphs that don't know
    // about projectType) still get human-readable labels for crystal
    // property keys like `thermalConductivity` → "Thermal Conductivity".
    return ProjectUtil.getCrystalPropertyName(property, l);
  }

  static getTypes(hidden: string[]): string[] {
    const a: string[] = [];
    if (!hidden?.includes('atomCount')) a.push('number');
    if (!hidden?.includes('bondCount')) a.push('number');
    if (!hidden?.includes('molecularMass')) a.push('number');
    if (!hidden?.includes('logP')) a.push('number');
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push('number');
    if (!hidden?.includes('hydrogenBondAcceptorCount')) a.push('number');
    if (!hidden?.includes('rotatableBondCount')) a.push('number');
    if (!hidden?.includes('polarSurfaceArea')) a.push('number');
    if (!hidden?.includes('heavyAtomCount')) a.push('number');
    if (!hidden?.includes('complexity')) a.push('number');
    if (!hidden?.includes('density')) a.push('number');
    if (!hidden?.includes('meltingPoint')) a.push('number');
    if (!hidden?.includes('boilingPoint')) a.push('number');
    if (!hidden?.includes('fsp3')) a.push('number');
    if (!hidden?.includes('ringCount')) a.push('number');
    if (!hidden?.includes('aromaticRingCount')) a.push('number');
    if (!hidden?.includes('qed')) a.push('number');
    return a;
  }

  static getDigits(hidden: string[]): number[] {
    const a: number[] = [];
    if (!hidden?.includes('atomCount')) a.push(0);
    if (!hidden?.includes('bondCount')) a.push(0);
    if (!hidden?.includes('molecularMass')) a.push(2);
    if (!hidden?.includes('logP')) a.push(2);
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push(0);
    if (!hidden?.includes('hydrogenBondAcceptorCount')) a.push(0);
    if (!hidden?.includes('rotatableBondCount')) a.push(0);
    if (!hidden?.includes('polarSurfaceArea')) a.push(1);
    if (!hidden?.includes('heavyAtomCount')) a.push(0);
    if (!hidden?.includes('complexity')) a.push(1);
    if (!hidden?.includes('density')) a.push(1);
    if (!hidden?.includes('meltingPoint')) a.push(1);
    if (!hidden?.includes('boilingPoint')) a.push(1);
    // Fsp3 / QED are bounded fractions, so two decimals carry the signal
    // without spurious precision. Ring counts are integers.
    if (!hidden?.includes('fsp3')) a.push(2);
    if (!hidden?.includes('ringCount')) a.push(0);
    if (!hidden?.includes('aromaticRingCount')) a.push(0);
    if (!hidden?.includes('qed')) a.push(2);
    return a;
  }

  static getTickIntegers(hidden: string[]): boolean[] {
    const a: boolean[] = [];
    if (!hidden?.includes('atomCount')) a.push(true);
    if (!hidden?.includes('bondCount')) a.push(true);
    if (!hidden?.includes('molecularMass')) a.push(false);
    if (!hidden?.includes('logP')) a.push(false);
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push(true);
    if (!hidden?.includes('hydrogenBondAcceptorCount')) a.push(true);
    if (!hidden?.includes('rotatableBondCount')) a.push(true);
    if (!hidden?.includes('polarSurfaceArea')) a.push(false);
    if (!hidden?.includes('heavyAtomCount')) a.push(true);
    if (!hidden?.includes('complexity')) a.push(false);
    if (!hidden?.includes('density')) a.push(false);
    if (!hidden?.includes('meltingPoint')) a.push(true);
    if (!hidden?.includes('boilingPoint')) a.push(true);
    if (!hidden?.includes('fsp3')) a.push(false);
    if (!hidden?.includes('ringCount')) a.push(true);
    if (!hidden?.includes('aromaticRingCount')) a.push(true);
    if (!hidden?.includes('qed')) a.push(false);
    return a;
  }

  static getUnits(hidden: string[]): string[] {
    const a: string[] = [];
    if (!hidden?.includes('atomCount')) a.push('');
    if (!hidden?.includes('bondCount')) a.push('');
    if (!hidden?.includes('molecularMass')) a.push('u');
    if (!hidden?.includes('logP')) a.push('');
    if (!hidden?.includes('hydrogenBondDonorCount')) a.push('');
    if (!hidden?.includes('hydrogenBondAcceptorCount')) a.push('');
    if (!hidden?.includes('rotatableBondCount')) a.push('');
    if (!hidden?.includes('polarSurfaceArea')) a.push('Å²');
    if (!hidden?.includes('heavyAtomCount')) a.push('');
    if (!hidden?.includes('complexity')) a.push('');
    if (!hidden?.includes('density')) a.push('g/ml');
    if (!hidden?.includes('meltingPoint')) a.push('°C');
    if (!hidden?.includes('boilingPoint')) a.push('°C');
    if (!hidden?.includes('fsp3')) a.push('');
    if (!hidden?.includes('ringCount')) a.push('');
    if (!hidden?.includes('aromaticRingCount')) a.push('');
    if (!hidden?.includes('qed')) a.push('');
    return a;
  }

  static getUnit(property: string): string {
    if (property === 'molecularMass') return 'u';
    if (property === 'polarSurfaceArea') return 'Å²';
    if (property === 'density') return 'g/ml';
    if (property === 'meltingPoint') return '°C';
    if (property === 'boilingPoint') return '°C';
    // Fall through to the crystal-property unit table so the molecule-side
    // helper resolves crystal keys (thermalConductivity → W/(m·K), etc.).
    const crystalUnit = ProjectUtil.getCrystalUnit(property);
    return crystalUnit;
  }

  static isExcluded(filters: Filter[], p: MolecularProperties, hidden: string[]): boolean {
    for (const f of filters) {
      if (hidden.includes(f.variable)) continue;
      if (f.type === FilterType.Between && f.upperBound !== undefined && f.lowerBound !== undefined) {
        const v = p[f.variable as keyof MolecularProperties];
        if (typeof v === 'number') {
          if (v > f.upperBound || v < f.lowerBound) return true;
        }
      }
    }
    return false;
  }

  // ─── Crystal-properties pipeline ───────────────────────────────────────
  // Mirror of getVariables/getTitles/... for the crystal-builder gallery.
  // The full bundle is large (~25 fields), so the parallel-coords plot
  // surfaces a focused default-visible subset and the user toggles the rest
  // via the property-selection popover.

  static getCrystalAllProperties(): string[] {
    return [
      'density',
      'meltingPoint',
      'boilingPoint',
      'sublimationPoint',
      'specificHeat',
      'thermalConductivity',
      'thermalExpansion',
      'debyeTemperature',
      'formationEnthalpy',
      'youngsModulus',
      'bulkModulus',
      'shearModulus',
      'poissonRatio',
      'mohsHardness',
      'vickersHardness',
      'bandGap',
      'electricalResistivity',
      'workFunction',
      'dielectricConstant',
      'refractiveIndex',
      'magneticSusceptibility',
      'curieTemperature',
      'molarMass',
      'atomsPerCell',
      'cellVolume',
      'packingFraction',
    ];
  }

  // Canonical default-visible properties for the crystal parallel-coords plot.
  // These are the materials-science basics that compare meaningfully across
  // metals, ceramics, and molecular crystals. Everything else stays hidden
  // by default and is opt-in via the property selector. Band gap is left OUT
  // of the general crystal default — most crystals built here are metals /
  // ceramics / molecular solids where it's 0 or irrelevant; the Semiconductor
  // Design project keeps it via getSemiconductorDefaultVisibleProperties().
  static getCrystalDefaultVisibleProperties(): string[] {
    return [
      'density',
      'meltingPoint',
      'specificHeat',
      'thermalConductivity',
      'youngsModulus',
      'bulkModulus',
      'mohsHardness',
    ];
  }

  // Default-hidden list for new / legacy crystal projects: everything in
  // getCrystalAllProperties() that's NOT in the canonical visible set.
  // Used when `projectState.hiddenCrystalProperties` is undefined / null.
  static getCrystalDefaultHiddenProperties(): string[] {
    const visible = new Set(ProjectUtil.getCrystalDefaultVisibleProperties());
    return ProjectUtil.getCrystalAllProperties().filter((k) => !visible.has(k));
  }

  // Semiconductor projects reuse the crystal property bundle but bias the
  // default-visible set toward electronic / optical properties. The set is
  // still mostly comparable to the crystal default (density + thermal
  // properties stay) so cross-project comparisons remain meaningful.
  static getSemiconductorDefaultVisibleProperties(): string[] {
    return [
      'bandGap',
      'dielectricConstant',
      'refractiveIndex',
      'workFunction',
      'electricalResistivity',
      'density',
      'meltingPoint',
      'thermalConductivity',
    ];
  }

  static getSemiconductorDefaultHiddenProperties(): string[] {
    const visible = new Set(ProjectUtil.getSemiconductorDefaultVisibleProperties());
    return ProjectUtil.getCrystalAllProperties().filter((k) => !visible.has(k));
  }

  // Resolve the effective hidden-property list for the crystal pipeline,
  // falling back to the canonical default when the project hasn't set one.
  // `projectType` is optional so existing callsites that don't differentiate
  // (older general crystal paths) keep their crystal-default behavior.
  // Pass it from callsites that need the semiconductor-biased default.
  static resolveCrystalHidden(hidden: string[] | null | undefined, projectType?: ProjectType): string[] {
    if (hidden) return hidden;
    return projectType === ProjectType.SEMICONDUCTOR_DESIGN
      ? ProjectUtil.getSemiconductorDefaultHiddenProperties()
      : ProjectUtil.getCrystalDefaultHiddenProperties();
  }

  // Effective visible-property list — full bundle filtered by the resolved
  // hidden-properties array. Honors both user toggles and the canonical
  // defaults.
  static getCrystalVariables(hidden: string[] | null | undefined): string[] {
    const effective = ProjectUtil.resolveCrystalHidden(hidden);
    return ProjectUtil.getCrystalAllProperties().filter((k) => !effective.includes(k));
  }

  static getCrystalPropertyName(property: string, l: { lng: string }): string | undefined {
    const key = 'crystalBuilder.prop_' + property;
    const translated = i18n.t(key, l);
    return translated && translated !== key ? translated : undefined;
  }

  static getCrystalTitles(hidden: string[], l: { lng: string }): string[] {
    return ProjectUtil.getCrystalVariables(hidden).map((v) => ProjectUtil.getCrystalPropertyName(v, l) ?? v);
  }

  static getCrystalUnits(hidden: string[]): string[] {
    return ProjectUtil.getCrystalVariables(hidden).map((v) => ProjectUtil.getCrystalUnit(v));
  }

  static getCrystalUnit(property: string): string {
    switch (property) {
      case 'density':
        return 'g/cm³';
      case 'meltingPoint':
      case 'boilingPoint':
      case 'sublimationPoint':
        return '°C';
      case 'specificHeat':
        return 'J/(g·K)';
      case 'thermalConductivity':
        return 'W/(m·K)';
      case 'thermalExpansion':
        return '10⁻⁶/K';
      case 'debyeTemperature':
      case 'curieTemperature':
        return 'K';
      case 'formationEnthalpy':
        return 'kJ/mol';
      case 'youngsModulus':
      case 'bulkModulus':
      case 'shearModulus':
      case 'vickersHardness':
        return 'GPa';
      case 'bandGap':
      case 'workFunction':
        return 'eV';
      case 'electricalResistivity':
        return 'Ω·m';
      case 'magneticSusceptibility':
        return '10⁻⁶ cm³/mol';
      case 'molarMass':
        return 'g/mol';
      case 'cellVolume':
        return 'Å³';
      default:
        return '';
    }
  }

  static getCrystalDigits(hidden: string[]): number[] {
    return ProjectUtil.getCrystalVariables(hidden).map((v) => {
      switch (v) {
        case 'atomsPerCell':
          return 0;
        case 'meltingPoint':
        case 'boilingPoint':
        case 'sublimationPoint':
        case 'debyeTemperature':
        case 'curieTemperature':
        case 'formationEnthalpy':
          return 1;
        case 'density':
        case 'specificHeat':
        case 'thermalConductivity':
        case 'mohsHardness':
        case 'youngsModulus':
        case 'bulkModulus':
        case 'shearModulus':
        case 'vickersHardness':
        case 'molarMass':
        case 'cellVolume':
          return 2;
        case 'poissonRatio':
        case 'bandGap':
        case 'workFunction':
        case 'refractiveIndex':
        case 'packingFraction':
          return 3;
        case 'electricalResistivity':
          return 4;
        default:
          return 2;
      }
    });
  }

  static getCrystalTickIntegers(hidden: string[]): boolean[] {
    return ProjectUtil.getCrystalVariables(hidden).map((v) => v === 'atomsPerCell');
  }

  static getCrystalTypes(hidden: string[]): string[] {
    return ProjectUtil.getCrystalVariables(hidden).map(() => 'number');
  }

  static getCrystalDefaultMin(property: string): number {
    switch (property) {
      case 'meltingPoint':
      case 'boilingPoint':
      case 'sublimationPoint':
        return -260;
      case 'formationEnthalpy':
        return -1700;
      case 'magneticSusceptibility':
        return -50;
      default:
        return 0;
    }
  }

  static getCrystalDefaultMax(property: string): number {
    switch (property) {
      case 'density':
        return 22;
      case 'meltingPoint':
      case 'boilingPoint':
        return 4000;
      case 'sublimationPoint':
        return 4000;
      case 'specificHeat':
        return 5;
      case 'thermalConductivity':
        return 2500;
      case 'thermalExpansion':
        return 80;
      case 'debyeTemperature':
        return 2300;
      case 'formationEnthalpy':
        return 500;
      case 'youngsModulus':
        return 1300;
      case 'bulkModulus':
        return 500;
      case 'shearModulus':
        return 600;
      case 'poissonRatio':
        return 0.5;
      case 'mohsHardness':
        return 10;
      case 'vickersHardness':
        return 110;
      case 'bandGap':
        return 14;
      case 'electricalResistivity':
        return 1e16;
      case 'workFunction':
        return 6;
      case 'dielectricConstant':
        return 2000;
      case 'refractiveIndex':
        return 4.5;
      case 'magneticSusceptibility':
        return 250;
      case 'curieTemperature':
        return 1500;
      case 'molarMass':
        return 400;
      case 'atomsPerCell':
        return 64;
      case 'cellVolume':
        return 600;
      case 'packingFraction':
        return 1;
      default:
        return 100;
    }
  }

  static isCrystalExcluded(
    filters: Filter[],
    p: { [k: string]: number | string | undefined | string[] },
    hidden: string[],
  ): boolean {
    for (const f of filters) {
      if (hidden.includes(f.variable)) continue;
      if (f.type === FilterType.Between && f.upperBound !== undefined && f.lowerBound !== undefined) {
        const v = p[f.variable];
        if (typeof v === 'number') {
          if (v > f.upperBound || v < f.lowerBound) return true;
        }
      }
    }
    return false;
  }
}
